import { useState, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import { IntegrationStatsBar } from "./components/IntegrationStatsBar";
import { ConfigureModal } from "./components/ConfigureModal";
import { ExotelConnectModal } from "./components/ExotelConnectModal";
import { WebsiteFormSection } from "./components/WebsiteFormSection";
import { IntegrationActivityTable } from "./components/IntegrationActivityTable";
import { SocialMediaSection } from "./components/SocialMediaSection";
import { LeadNormalizationSection } from "./components/LeadNormalizationSection";
import { IntegrationHealthSection } from "./components/IntegrationHealthSection";
import { LeadSourceContainers } from "./components/LeadSourceContainers";
import { telephonyIntegrations, activityLog } from "@/mocks/integrationsData";
import type { Integration, IntegrationStatus } from "@/mocks/integrationsData";
import { fetchApi } from "@/services/apiService";

const statusBadgeConfig: Record<IntegrationStatus, { label: string; classes: string }> = {
  connected: { label: "Connected", classes: "bg-green-50 text-green-700 border border-green-200" },
  not_connected: { label: "Not Connected", classes: "bg-gray-100 text-[#6b7280] border border-gray-200" },
  error: { label: "Error", classes: "bg-red-50 text-red-600 border border-red-200" },
};

const cardBorderMap: Record<IntegrationStatus, string> = {
  connected: "border-[#e5e7eb] border-t-2 border-t-green-400",
  not_connected: "border-[#e5e7eb]",
  error: "border-red-300",
};

const activityTypeIcons: Record<string, string> = {
  webhook: "ri-link text-green-600",
  test: "ri-flask-line text-indigo-600",
  sync: "ri-refresh-line text-blue-600",
  error: "ri-error-warning-line text-red-600",
  assign: "ri-user-received-line text-amber-600",
  config: "ri-settings-3-line text-gray-600",
};

export default function IntegrationsPage() {
  const { showToast } = useToast();
  const [configuring, setConfiguring] = useState<Integration | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [telephonyList, setTelephonyList] = useState(telephonyIntegrations);

  useEffect(() => {
    fetchApi<typeof telephonyIntegrations>('/integrations', undefined, telephonyIntegrations)
      .then((data) => {
        if (data && data.length > 0) setTelephonyList(data as typeof telephonyIntegrations);
      })
      .catch((err) => console.error("Error fetching integrations:", err));
  }, []);

  const handleTelephonyConnected = (name: string) => {
    fetchApi(`/integrations/${name}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'connected' })
    }).catch(err => console.error("Error updating integration status:", err));

    setTelephonyList((prev) =>
      prev.map((t) =>
        t.id === name
          ? { ...t, status: "connected" as IntegrationStatus, lastSync: "Just now" }
          : t
      )
    );
  };

  return (
    <CrmLayout title="Integrations">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#111827]">Integrations</h2>
          <p className="text-sm text-[#6b7280] mt-1">
            Connect your lead sources and communication tools to CRM Pro
          </p>
        </div>
        <button
          onClick={() => setShowActivityLog(true)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
          <i className="ri-history-line" />
          Activity Log
        </button>
      </div>

      {/* Stats */}
      <IntegrationStatsBar />

      {/* Lead Source Integrations */}
      <LeadSourceContainers />

      {/* Section 5: Telephony &amp; Call Integration */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-[#111827]">Telephony &amp; Call Integration</h3>
          <p className="text-sm text-[#6b7280] mt-0.5">
            Connect your call provider to enable click-to-call, auto call logging and recordings
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {telephonyList.map((tel) => {
            const badge = statusBadgeConfig[tel.status];

            return (
              <div
                key={tel.id}
                className={`bg-white rounded-xl p-5 flex flex-col ${cardBorderMap[tel.status]}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg ${tel.logoBg} flex items-center justify-center flex-shrink-0`}
                    >
                      <span className="text-sm font-bold text-white">
                        {tel.logoLetters}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#111827]">{tel.name}</h4>
                      <p className="text-xs text-[#6b7280]">{tel.type}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${badge.classes}`}
                  >
                    {badge.label}
                  </span>
                </div>

                {tel.status === "connected" && (
                  <div className="flex-1 space-y-2 mb-4">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#6b7280]">Last sync</span>
                      <span className="text-[#6b7280]">{tel.lastSync}</span>
                    </div>
                  </div>
                )}

                {tel.status === "not_connected" && (
                  <div className="flex-1 mb-4">
                    <p className="text-xs text-[#6b7280] leading-relaxed">{tel.description}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100">
                  {tel.status === "connected" && (
                    <>
                      <button
                        onClick={() => setConfiguring(tel)}
                        className="flex-1 px-3 py-1.5 border border-indigo-600 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
                      >
                        Configure
                      </button>
                      <button
                        onClick={() => {
                          setTelephonyList((prev) =>
                            prev.map((t) =>
                              t.id === tel.id
                                ? { ...t, status: "not_connected" as IntegrationStatus }
                                : t
                            )
                          );
                          showToast(`${tel.name} disconnected`, "info");
                        }}
                        className="text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap"
                      >
                        Disconnect
                      </button>
                    </>
                  )}
                  {tel.status === "not_connected" && (
                    <button
                      onClick={() => {
                        if (tel.id === "exotel") {
                          setConnecting("exotel");
                        } else {
                          setTelephonyList((prev) =>
                            prev.map((t) =>
                              t.id === tel.id
                                ? { ...t, status: "connected" as IntegrationStatus, lastSync: "Just now" }
                                : t
                            )
                          );
                          showToast(`${tel.name} connected successfully!`, "success");
                        }
                      }}
                      className="w-full px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 6: Website Form Integration */}
      <WebsiteFormSection />

      {/* Section 8: Social Media Leads */}
      <SocialMediaSection />

      {/* Section 9: Lead Normalization Rules */}
      <LeadNormalizationSection />

      {/* Section 10: Integration Health */}
      <IntegrationHealthSection />

      {/* Section 7: Integration Activity Log */}
      <div id="integration-activity-log">
        <IntegrationActivityTable />
      </div>

      {/* Configure Modal */}
      {configuring && (
        <ConfigureModal
          integration={configuring}
          onClose={() => setConfiguring(null)}
        />
      )}

      {/* Connect Modal (Exotel) */}
      {connecting === "exotel" && (
        <ExotelConnectModal
          onClose={() => setConnecting(null)}
          onConnected={() => handleTelephonyConnected("exotel")}
        />
      )}

      {/* Activity Log Panel */}
      {showActivityLog && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowActivityLog(false)} />
          <div className="relative w-full max-w-md h-full bg-white shadow-xl flex flex-col animate-slide-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-[#111827]">Activity Log</h3>
              <button
                onClick={() => setShowActivityLog(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                {activityLog.map((entry, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="mt-0.5 flex-shrink-0">
                      <i className={`${activityTypeIcons[entry.type] || "ri-information-line text-gray-400"} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#374151]">{entry.event}</p>
                      <p className="text-xs text-[#9ca3af] mt-0.5">{entry.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </CrmLayout>
  );
}